/** @typedef {typeof import("./types")} */
/** @typedef {typeof import("./non-critical")} */

/* Utils */

theme.shop.currency_extra_formats = undefined;
/**
 * Formats a number using the cart currency
 *
 * @param {number} cents Amount in cents
 * @param {string} [round="ceil | floor | round"]
 * @return {string} The formated number in the cart currency
 */
const formatMoney = function (
  cents,
  round = "round",
  currency = null,
  removeSpace = false,
) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  let formatString = theme.shop.money_format;
  if (currency !== null) {
    let currencySymbol;
    if (
      theme.cart.currency_format_object[currency] !== undefined &&
      theme.cart.currency_format_object[currency] !== null
    ) {
      currencySymbol = theme.cart.currency_format_object[currency];
    } else {
      currencySymbol = currency;
    }
    formatString = `${currencySymbol} {{amount}}`;
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return "0";
    }
    number = number / 100;

    switch (round) {
      case "ceil":
        number = Math.ceil(number);
        break;
      case "floor":
        number = Math.floor(number);
        break;
      default:
        number = number.toFixed(precision);
    }

    const parts = String(number).split(".");
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands,
    );
    const cents = parts[1] ? decimal + parts[1] : "";
    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  if (removeSpace) {
    formatString = formatString.replace(/\s/gi, "");
  }
  return formatString.replace(placeholderRegex, value).toUpperCase();
};

const logger = {
  info: (message) => {
    window.Sentry && window.Sentry.captureMessage(message);
  },
  error: (err) => {
    window.Sentry && window.Sentry.captureException(err);
  },
};

/**
 * Formats a date into a short version of it
 *
 * @param {Date} date Date to format
 * @param {string} delimiter delimiter for format default: /
 * @return {string} formated date  M / D / Y
 */
function formatDateShort(date, delimiter = "/") {
  const dateObject = new Date(date);
  const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(
    dateObject,
  );
  const month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(
    dateObject,
  );
  const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(
    dateObject,
  );
  return `${month}${delimiter}${day}${delimiter}${year}`;
}
function getDateTranslated(dateString = "") {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(
    `${theme.shop.language}-${theme.cart.country_code}`,
    {
      day: "numeric",
      month: "long",
      weekday: "long",
      year: "numeric",
    },
  ).format(date);
}
/**
 * @param {htmlElement} htmlElement
 * @param {{}} replaceData key value to replace
 */
function replaceMultipleInnerText(htmlElement, replaceData = {}) {
  // accepts array of objects with key being replacement value and value the replaced values
  if (!htmlElement || !replaceData || !Object.entries(replaceData).length)
    return;
  Object.entries(replaceData).forEach(([key, value]) => {
    htmlElement.innerHTML = htmlElement.innerHTML.replaceAll([key], [value]);
  });
}

/**
 * Asynchronously load a script
 * @param {string} src
 * @param {string} type
 * @returns Promise
 */
window.loadedScripts = {};
const loadScript = (src, type = "text/javascript") => {
  const head = document.querySelector("head");
  if (!window.loadedScripts[src]) {
    window.loadedScripts[src] = new Promise((r) => {
      let l = false;
      const script = document.createElement("script");
      script.type = type;
      script.src = src;
      script.crossorigin = "anonymous";
      script.async = true;
      script.onload = script.onreadystatechange = function () {
        if (!l && (!this.readyState || this.readyState === "complete")) {
          l = true;
          r();
        }
      };
      head.appendChild(script);
    });
  }
  return window.loadedScripts[src];
};

/**
 * Returns the path after adding the new query param to it
 * @param path — The current path
 * @param key — The query param key
 * @param value — The query param value
 */
function addQueryParam(path = "", key = "", value = "") {
  let finalPath = path;
  if (finalPath.indexOf("?") !== -1) {
    finalPath = finalPath + `&${key}=${value}`;
  } else {
    finalPath = finalPath + `?${key}=${value}`;
  }
  return finalPath;
}

function parseSearchParams(search) {
  if (!search) {
    return {};
  }

  return search
    .replace(/^\?/, "")
    .split("&")
    .map((param) => param.split("="))
    .reduce(
      (acc, [k, v]) => ({
        ...acc,
        [window.decodeURIComponent(k)]: window.decodeURIComponent(v),
      }),
      {},
    );
}

const apiFetch = (BASE_URL, path, opts) => {
  return fetch(`${BASE_URL}${path}`, opts);
};

const apiFetchJson = async (BASE_URL, path, opts) => {
  const req = await fetch(`${BASE_URL}${path}`, opts);
  return req.json();
};

/**
 * Fetches text from an API endpoint
 *
 * @param {string} BASE_URL - The base URL of the API
 * @param {string} path - The endpoint path (e.g. "/cart")
 * @param {RequestInit} [opts] - Optional fetch configuration
 * @returns {Promise<string>} The response body as text
 */

const apiFetchText = async (BASE_URL, path, opts) => {
  const req = await fetch(`${BASE_URL}${path}`, opts);
  return req.text();
};

const apiFetchStorefront = async (body, method = "POST") => {
  return apiFetchJson("", "/api/2025-04/graphql.json", {
    headers: {
      "x-shopify-storefront-access-token":
        theme.settings.storefront_access_token,
      "Content-Type": "application/json",
    },
    method,
    body,
  });
};

const apiFetchProxy = async (path, headers) => {
  return apiFetchJson(
    theme.settings.pmall_brand_services_proxy_url,
    path,
    headers,
  );
};

/**
 * This function should not be used directly. Prefer one of the more specific
 * functions below.
 *
 * @param data {[key: string] :  any } - The data to push into the datalayer. e.g
 * @returns {Promoise<void>}
 */
const layerPush = (data) => {
  const c = getCookie("disable_analytics");
  if (c) return;
  return new Promise((resolve) => {
    data.codeSource = theme.settings.analytics_code_source;
    data.exchangeRate = theme.cart.currency_with_vat;

    const currentCallback = data.eventCallback;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign({}, data, {
        eventCallback: () => {
          if (typeof currentCallback === "function") {
            currentCallback();
          }
          resolve();
        },
      }),
    );

    if (!window.google_tag_manager) {
      if (typeof currentCallback === "function") {
        currentCallback();
      }
      resolve();
    }

    // Send all events to Pangaea Analytics
    window._pgAnalytics = window._pgAnalytics || [];
    window._pgAnalytics.push(data);
  });
};

const trackAmazonCollectionCTA = (amazonProductTitle) => {
  return layerPush({
    event: "addToCartAmazon",
    amazonProductTitle,
  });
};

/**
 * This event is triggered each time we visit a page.
 * @param {PageView} pageData Refer to the PageView defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackPageView = (pageData) => {
  return layerPush({
    event: "pageView",
    page: pageData,
  });
};

/**
 * Push an event or user data to Optimizely
 * @param {object} data
 */
const optimizelyPush = (data) => {
  window["optimizely"] = window["optimizely"] || [];
  window["optimizely"].push(data);
};

const isAmplitudeAvailable = () => {
  return window.amplitude && window.amplitude.getDeviceId();
};

const getDeviceId = () => {
  return isAmplitudeAvailable() ? window.amplitude.getDeviceId() : getUUID();
};

const triggerExperiment = (experimentKey) => {
  if (isAmplitudeAvailable() && window.growthbookInstance) {
    window.growthbookInstance.triggerExperiment(experimentKey);
  }
};

/**
 * This event is triggered each time we visit a page.
 * @param {EcommerceImpression} impressions Refer to the EcommerceImpression defination in the DATATYPES above
 * @param {currencyCode} string  The cart currency
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductImpression = (impressions, currencyCode) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceImpression",
    ecommerce: { impressions, currencyCode },
  });
};

/**
 * This event is triggered each time we click on a product link that takes us to a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductClick = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceProductClick",
    ecommerce: {
      click: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time we visit a product page
 * @param {EcommerceProduct} product Refer to the EcommerceProduct defination in the DATATYPES above
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 */
const trackProductDetail = (product) => {
  layerPush({ ecommerce: null });
  return layerPush({
    event: "ecommerceDetailPage",
    ecommerce: {
      detail: { products: [product] },
    },
  });
};

/**
 * This event is triggered each time the user state is updated
 * @param {Object} traits
 */
const trackUserTraits = (traits) => {
  layerPush({
    event: "identifiedUserTraits",
    traits,
  });
};

const showLoader = (show = true) => {
  document.body.classList.add("overflow-hidden");
  const modalLoader = document.querySelector("#modalPreloader");
  if (show) {
    return modalLoader.classList.add("loading", "d-flex");
  }
  modalLoader.classList.remove("loading", "d-flex");
  document.body.classList.remove("overflow-hidden");
};

const openModal = (eventSource) => {
  const modalSelector = eventSource.getAttribute("data-target");
  const modalSectionId = eventSource.getAttribute("data-target-section-id");
  const modalScriptUrl = eventSource.getAttribute("data-target-script-url");

  const fetchModal = () => {
    document.body.classList.add("overflow-hidden");
    showLoader();
    apiFetchText("", `?section_id=${modalSectionId}`)
      .then((response) => {
        document.querySelector("#modal-container").innerHTML = response;
        const returnInput = document.querySelector('input[name="return_to"]');
        if (returnInput) returnInput.value = returnInput.value.split("?")[0];
        const modalScript = document.createElement("script");
        modalScript.src = modalScriptUrl;
        document.head.append(modalScript);
      })
      .finally(() => {
        showLoader(false);
      });
  };

  const modal = document.querySelector(modalSelector);
  if (modal) {
    return modal.showModal();
  }
  fetchModal();
};

if (!customElements.get("convert-to-cart-currency")) {
  customElements.define(
    "convert-to-cart-currency",
    class ConvertToCartCurrency extends HTMLElement {
      constructor() {
        super();
      }
      connectedCallback() {
        const roundingMethod = this.getAttribute("data-round");
        const value = this.getAttribute("data-value");
        const suffix = this.getAttribute("data-suffix") || "";

        let rate = theme.cart.currency_with_vat;
        const externalRate = theme.shop.external_exchange_rate;
        if (
          this.getAttribute("data-rate") === "external" &&
          Shopify &&
          Shopify.currency &&
          Shopify.currency.rate &&
          externalRate
        ) {
          // Use VAT from currency-convert product
          rate = (rate / Shopify.currency.rate) * externalRate;
        }
        const amount = formatMoney(
          Number(value) * Number(rate),
          roundingMethod,
        );
        this.innerHTML = `${amount}${suffix}`;
      }
    },
  );
}
/**
 * use to async await some time
 * @returns {Promise<void>} We do not need to wait for the promise to resolve
 * @param ms: milliseconds to wait until resolve
 */
function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
const mobileQuery = window.matchMedia("(max-width: 991px)");
const isMobile = () => mobileQuery.matches;

// Important Utility Functions
async function updateCart(payload, action = "update") {
  return apiFetchJson(window.Shopify.routes.root, `cart/${action}.js`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
}

function parseJSON(jsonStr = "", fallback = {}) {
  try {
    return JSON.parse(jsonStr);
  } catch (error) {
    return fallback;
  }
}

function isEmpty(value) {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === "" || value === null || value === undefined;
}

function cleanObject(object = {}) {
  const cleanedObject = {};
  Object.entries(object).forEach(([key, value]) => {
    if (!isEmpty(value)) {
      cleanedObject[key] = value;
    }
  });
  return cleanedObject;
}

window.addEventListener("load", () => {
  document.body.classList.add("loaded");
});
function isFacebookBrowser() {
  return /FBAN|FBAV/.test(navigator.userAgent);
}
window.addEventListener("load", () => {
  if (isFacebookBrowser()) {
    var style = document.createElement("style");
    style.innerHTML = `
        html, body {
            height: 100vh !important;
            overflow-y: auto !important;
            scroll-snap-type: none !important;
            scroll-behavior: auto !important;
        }
    `;
  }
});

document.fonts.addEventListener("loadingdone", () => {
  document.body.classList.add("font-loaded");
});

/**
 * @param {{ key: string, value: string | number | boolean | null | undefined }[]} params
 * @param {string | URL} [base=window.location.href]
 * @returns {URL}
 */
const buildUrlWithQuery = (params = {}, base = window.location.href) => {
  const url = new URL(base);
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, String(value));
    }
  });
  return url;
};

const batchUpdate = (fn) => {
  setTimeout(() => requestAnimationFrame(fn), 0);
};

class BaseHTMLElement extends HTMLElement {
  constructor() {
    super();
  }
  init = () => {};
  connectedCallback() {
    batchUpdate(this.init);
  }
}

class LazySection extends BaseHTMLElement {
  cleanUp = () => {
    this.itemsToRemove.forEach((item) => item.remove());
  };

  async load() {
    const requestPath = this.getAttribute("data-request-path");
    const sectionId = this.getAttribute("data-section-id");
    const targetSelector = this.getAttribute("data-target");
    const removeSelector = this.getAttribute("data-remove-target");

    this.itemsToRemove = [...document.querySelectorAll(removeSelector), this];

    if (!(requestPath && sectionId)) {
      this.cleanUp();
      return;
    }

    try {
      const url = new URL(requestPath, location.origin);
      url.searchParams.set("section_id", sectionId);

      const sectionContent = await apiFetchText("", url.toString());
      const parsedDoc = document
        .createRange()
        .createContextualFragment(sectionContent);

      let content = [parsedDoc.firstElementChild];
      if (targetSelector) {
        content = parsedDoc.querySelectorAll(targetSelector);
      }

      const root = this.closest("[data-role=lazySectionRoot]") ?? this;
      root.replaceWith(...content);
    } catch (error) {
      logger.error("LazySection failed:" + error);
    }
    this.cleanUp();
  }

  init = () => {
    /** @type {"manual" | null } */
    const trigger = this.getAttribute("data-trigger");

    if (trigger === "manual") {
      this.triggerButton = this.querySelector("[data-role=paginationButton]");
      this.triggerButton?.addEventListener("click", this.handleTrigger);
    } else {
      this.load();
    }
  };

  /**
   * @param {Event & { currentTarget: HTMLButtonElement }} e
   */
  handleTrigger = (e) => {
    e.preventDefault();
    this.classList.add("form-submitting");
    this.load();
  };

  disconnectedCallback() {
    this.triggerButton?.removeEventListener("click", this.handleTrigger);
  }
}

customElements.define("lazy-section", LazySection);

const Cart = {
  /**
   * @param {string} content
   * @param {string} targetId
   */
  _renderHtml: (content, targetSelector, rootSelector = ".shopify-section") => {
    const root = document.createRange().createContextualFragment(content);
    const htmlContent = root.querySelector(rootSelector)?.innerHTML;
    const targetElement = document.querySelector(targetSelector);
    if (targetElement) {
      targetElement.innerHTML = htmlContent;
    }
  },

  /**
   * @param {AddToCartSections} sections
   */
  render: (sections) => {
    Cart._renderHtml(sections["cart-icon-bubble"], "#cart-icon-bubble");
    Cart._renderHtml(
      sections["cart-drawer-disable-byob"],
      "#cart-drawer .drawer__inner",
      ".shopify-section .drawer__inner",
    );
  },

  /**
   * @param {CartItem} addedCartItem
   * @param {FormData} formData
   */
  trackAddToCart: (addedCartItem, formData) => {
    try {
      const itemProperties = addedCartItem.properties || {};
      const ecommerceProduct = {
        id: addedCartItem.product_id,
        name: addedCartItem.product_title,
        variant: addedCartItem.variant_id,
        variant_title: addedCartItem.variant_title,
        price: addedCartItem.price / 100,
        currency: theme.cart.currency, // must be the customer's selected currency not shop currency
        quantity: formData.get("items[0][quantity]") || 1, // must be the quanity added not the total quantity
        brand: addedCartItem.vendor,
        is_free_trial: Boolean(itemProperties._is_free_trial),
        item_type: itemProperties._item_type,
        upsell_type: formData.get("upsellType"),
        category: addedCartItem.product_type,
        subscription_interval:
          addedCartItem.selling_plan_allocation?.selling_plan.options[0]?.value,
        sellingPlanId:
          addedCartItem?.selling_plan_allocation?.selling_plan?.id || null,
      };

      layerPush({ ecommerce: null });

      return layerPush({
        event: "ecommerceCartAdd",
        ecommerce: {
          add: { products: [ecommerceProduct] },
        },
      });
    } catch (e) {
      logger.error(`Error tracking AddToCart: ${e}`);
    }
  },
  open: async () => {
    return new Promise(async (resolve, _) => {
      if (typeof openDrawer === "function") {
        await openDrawer();
        resolve();
      } else {
        window.addEventListener("load", async () => {
          await openDrawer();
          resolve();
        });
      }
    });
  },
};

class OptionSelector extends BaseHTMLElement {
  inputSelector = null;
  /**
   * @param {boolean} isSelected
   */
  afterChange = (isSelected) => {};

  afterInit = () => {};

  /**
   * @param {Event & { target: HTMLInputElement }} e
   */
  _handleInputChange = (e) => {
    if (e.target.name === this.input?.name) {
      const isSelected = e.target.value === this.input?.value;
      this.afterChange(isSelected);
      if (isSelected) {
        this.context?.update(this.contextData);
      }
    }
  };

  init = () => {
    this.contextData = parseJSON(
      this.querySelector("[data-role=contextData]")?.innerHTML,
    );

    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector(
      this.inputSelector ?? this.getAttribute("data-input-selector"),
    );

    /** @type {ProductContext | null} */
    this.context = this.closest("product-context");

    if (this.input.checked) {
      this.context?.update(this.contextData);
    }

    this.afterInit();

    document.addEventListener("change", this._handleInputChange);
  };
  disconnectedCallback() {
    document.removeEventListener("change", this._handleInputChange);
  }
}

class AddToCartButton extends BaseHTMLElement {
  /**
   * @param {string} message
   */
  updateErrorMessage = (message = "") => {
    const errorElement = this.querySelector("[data-role=errorMessage]");
    if (errorElement) {
      errorElement.textContent = message;
    }
  };

  proceedToCheckout = async () => {
    const checkoutButton = document.createElement("checkout-button");
    return checkoutButton.processCheckout();
  };

  /**
   * @param {FormDataEvent } e
   */
  handleSubmit = async (e) => {
    try {
      e.preventDefault();
      this.updateErrorMessage("");
      this.button.parentElement.classList.add("form-submitting");
      const formData = new FormData(this.form);
      formData.append("sections", "cart-drawer-disable-byob,cart-icon-bubble");

      /** @type {ProductContext | null} */
      const context =
        document.getElementById(this.contextId) ??
        this.closest("product-context");
      const mainVariantId = context?.state?.main_variant_id;

      formData.set(
        "items[0][id]",
        mainVariantId ?? formData.get("items[0][id]"),
      );

      const basePath = Shopify.routes.root;

      /** @type {AddToCartResponse} */
      const cartResponse = await apiFetchJson(basePath, "cart/add.js", {
        method: "POST",
        body: formData,
      });

      // TODO: Handle 429 error and call this.form.submit() so the browser handles the error
      // TODO: Handle 422 error and refresh the cart
      if (!cartResponse.sections) {
        throw cartResponse;
      }
      Cart.trackAddToCart(cartResponse.items?.[0] ?? cartResponse, formData);

      if (this.action == "accelerated_checkout") {
        await this.proceedToCheckout();
      } else {
        Cart.render(cartResponse.sections);
        Cart.open();
        this.button.parentElement.classList.remove("form-submitting");
        this.closest("dialog-modal")?.close();
      }
    } catch (error) {
      const message = error?.description || theme.translations.errors.cartError;
      this.updateErrorMessage(message);
      this.button.parentElement.classList.remove("form-submitting");
    }
  };

  init = () => {
    const formId = this.getAttribute("data-target");
    this.contextId = this.getAttribute("data-context-id");

    /** @type {"add_to_cart" | "accelerated_checkout"} */
    this.action = this.getAttribute("data-role") ?? "add_to_cart";

    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector("button[type=submit]");

    /** @type {HTMLFormElement | null} */
    this.form = document.getElementById(formId) ?? this.closest("form");

    this.button?.addEventListener("click", this.handleSubmit);
  };
  disconnectedCallback() {
    this.button?.removeEventListener("click", this.handleSubmit);
  }
}

customElements.define("add-to-cart-button", AddToCartButton);

class ProductContext extends HTMLElement {
  #state = {};
  #subs = Object.create(null);

  connectedCallback() {
    if (this.id) {
      document.dispatchEvent(
        new CustomEvent("product-context:connected", {
          detail: { id: this.id, element: this },
        }),
      );
    }
  }

  update(data = {}) {
    for (const key in data) {
      // Skip for fields that did not change
      if (this.#state[key] === data[key]) {
        continue;
      }
      this.#state[key] = data[key];
      const listeners = this.#subs[key];
      if (listeners) {
        listeners.forEach((fn) => fn(data[key]));
      }
    }
  }
  subscribe(field, fn) {
    (this.#subs[field] ??= new Set()).add(fn);
    if (field in this.#state) {
      fn(this.#state[field]);
    }
    return () => this.#subs[field].delete(fn);
  }
  get state() {
    return this.#state;
  }
}
customElements.define("product-context", ProductContext);

class ContextData extends BaseHTMLElement {
  #controller = null;
  #contextReconnectHandler = null;

  static cache = new Map();
  static pending = new Map();
  static maxCache = 10;

  static get(key) {
    return this.cache.get(key);
  }
  static has(key) {
    return this.cache.has(key);
  }
  static set(key, value) {
    this.cache.set(key, value);
    if (this.cache.size > this.maxCache) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  static async fetch(key, fetchFn) {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }
    if (this.pending.has(key)) {
      return this.pending.get(key);
    }
    const promise = fetchFn().then((res) => {
      this.set(key, res);
      this.pending.delete(key);
      return res;
    });
    this.pending.set(key, promise);
    return promise;
  }

  updateSection = async (baseUrl) => {
    const current = document.querySelector(this.targetValue);
    try {
      if (!baseUrl || !this.sectionId || !this.targetValue) {
        return;
      }

      const sectionRequestUrl = buildUrlWithQuery(
        { section_id: this.sectionId },
        baseUrl,
      ).href;

      this.#controller?.abort();
      this.#controller = new AbortController();
      if (ContextData.has(sectionRequestUrl)) {
        this.#apply(ContextData.get(sectionRequestUrl), current);
        return;
      }
      current?.classList.add("blink");
      const html = await ContextData.fetch(sectionRequestUrl, () =>
        apiFetchText(sectionRequestUrl, "", {
          signal: this.#controller.signal,
        }),
      );
      this.#apply(html, current);
    } catch (error) {
      if (error.name !== "AbortError") {
        logger.error(`Section request error: ${error}`);
      }
    } finally {
      current?.classList.remove("blink");
    }
  };

  /**
   * Replaces DOM content
   * @param {HTMLElement} html
   * @param {HTMLElement} current
   * @returns
   */
  #apply(html, current) {
    if (!current) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const incoming = doc.querySelector(this.targetValue);
    if (!incoming) return;
    if (current.innerHTML === incoming.innerHTML) return;
    batchUpdate(() => {
      current.innerHTML = incoming.innerHTML;
    });
  }

  #bindContext(context, field, target, parentElement) {
    this.unsubscribe?.();
    this.unsubscribe = context?.subscribe(field, (value) => {
      switch (target) {
        case "class":
          this.classList.toggle(this.targetValue, !value);
          break;

        case "block":
          this.updateSection(value);
          break;

        default:
          if (this.innerHTML !== value) {
            this.innerHTML = value;
            parentElement.classList.toggle("hidden", !value?.toString().length);
          }
          break;
      }
    });
  }

  init = () => {
    const rootSelector = this.getAttribute("data-root");
    const field = this.getAttribute("data-field");
    const target = this.getAttribute("data-target");
    const contextId = this.getAttribute("data-context-id");

    this.targetValue = this.getAttribute("data-target-value");
    this.sectionId = this.getAttribute("data-section-id");

    /** @type {ProductContext | null} */
    const context =
      document.getElementById(contextId) ?? this.closest("product-context");

    let parentElement = this.parentElement;
    if (rootSelector) {
      parentElement = this.closest(rootSelector) ?? this.parentElement;
    }

    this.#bindContext(context, field, target, parentElement);

    // When using contextId, the referenced product-context may be inside a
    // section that gets replaced on variant change. Re-subscribe whenever a
    // new product-context with the same ID connects to the DOM.
    if (contextId && !this.#contextReconnectHandler) {
      this.#contextReconnectHandler = (e) => {
        if (e.detail.id === contextId) {
          this.#bindContext(e.detail.element, field, target, parentElement);
        }
      };
      document.addEventListener(
        "product-context:connected",
        this.#contextReconnectHandler,
      );
    }
  };

  disconnectedCallback() {
    this.unsubscribe?.();
    this.#controller?.abort();
    if (this.#contextReconnectHandler) {
      document.removeEventListener(
        "product-context:connected",
        this.#contextReconnectHandler,
      );
      this.#contextReconnectHandler = null;
    }
  }
}
customElements.define("context-data", ContextData);

class VariantSelector extends OptionSelector {
  inputSelector = ".product-option__input";

  afterInit = () => {
    const formId = this.getAttribute("data-target");
    this.form = document.getElementById(formId) ?? this.closest("form");
    this.variantId = this.getAttribute("data-variant-id");
    this.updateUrl = this.getAttribute("data-update-url") === "true";
  };

  getBaseUrl = () => {
    let baseUrl = undefined;
    const requestPath = this.closest("[data-role=requestRoot]")?.getAttribute(
      "data-request-path",
    );
    if (requestPath) {
      baseUrl = new URL(`${window.location.origin}${requestPath}`);
    }
    return baseUrl;
  };

  /**
   *
   * @param {{key: string, value: string}} pathSet
   */
  getDynamicPaths = (pathSet = {}) => {
    return Object.entries(pathSet)
      .filter(([key, value]) => !!value)
      .map(([key, value]) => `${key}:${value}`)
      .join(";");
  };

  afterChange = (isSelected) => {
    if (isSelected) {
      const browserUrl = buildUrlWithQuery(
        { variant: this.variantId },
        this.getBaseUrl(),
      );

      if (this.updateUrl) {
        history.replaceState({}, "", browserUrl);
      }

      const formData = this.form && new FormData(this.form);
      const sellingPlan = formData?.get("items[0][selling_plan]");
      const quantity = formData?.get("items[0][quantity]");
      const purchaseOptionHandle = this.context?.state?.purchase_option_handle;

      const variantUrl = new URL(browserUrl);
      const dynamicPath = this.getDynamicPaths({
        quantity: quantity,
        selling_plan_id: sellingPlan,
        purchase_option_handle: purchaseOptionHandle,
        api_request_source: "variant_selector",
      });
      variantUrl.pathname = `${variantUrl.pathname}/${dynamicPath}`;
      this.context.update({ variant: variantUrl.href });
    }
  };
}
customElements.define("variant-selector", VariantSelector);

class PurchaseOption extends OptionSelector {
  inputSelector = ".purchase-option__input";

  afterChange = (isSelected) => {
    this.fieldsetControls.forEach((fieldset) =>
      fieldset.toggleAttribute("disabled", !isSelected),
    );
  };
  /**
   * @param {Event & { target: HTMLButtonElement }} e
   */
  handleSwitchOption = (e) => {
    e.stopPropagation();
    const otherOptionSelector = `input[name="${this.input.name}"]:not([value="${this.input.value}"])`;

    /** @type {HTMLInputElement | null} */
    const otherOption = this.parentElement.querySelector(otherOptionSelector);
    otherOption?.click();
  };

  afterInit = () => {
    this.fieldsetControls = this.querySelectorAll(
      "[data-role=fieldsetControl]",
    );

    /** @type {HTMLButtonElement | null} */
    this.optionSwitchBtn = this.querySelector(
      "[data-role=purchaseOptionSwitcher]",
    );

    this.optionSwitchBtn?.addEventListener("click", this.handleSwitchOption);
  };
  disconnectedCallback() {
    super.disconnectedCallback();
    this.optionSwitchBtn?.removeEventListener("click", this.handleSwitchOption);
  }
}
customElements.define("purchase-option", PurchaseOption);

class StickyReveal extends BaseHTMLElement {
  init = () => {
    const selector = this.getAttribute("data-target");
    this.target = document.querySelector(selector);
    if (!this.target) return;

    this._observer = new IntersectionObserver(
      ([entry]) => {
        const pastTarget =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle("hidden", !pastTarget);
      },
      { rootMargin: "-1px 0px 0px 0px" },
    );

    this._observer.observe(this.target);
  };

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
customElements.define("sticky-reveal", StickyReveal);

class QuantitySelector extends BaseHTMLElement {
  increaseQuantity = () => {
    this.qtyInput?.stepUp();
  };
  reduceQuantity = () => {
    this.qtyInput?.stepDown();
  };
  handleBlur = () => {
    if (this.qtyInput.value.trim() === "") {
      this.qtyInput.value = "1";
    }
  };
  init = () => {
    /** @type {HTMLButtonElement | null} */
    this.reduceQtyBtn = this.querySelector("[data-role=reduceQty]");

    /** @type {HTMLButtonElement | null} */
    this.increaseQtyBtn = this.querySelector("[data-role=increaseQty]");

    /** @type {HTMLInputElement | null} */
    this.qtyInput = this.querySelector("[data-role=qtyInput]");

    this.reduceQtyBtn?.addEventListener("click", this.reduceQuantity);
    this.increaseQtyBtn?.addEventListener("click", this.increaseQuantity);
    this.qtyInput.addEventListener("blur", this.handleBlur);
  };

  disconnectedCallback() {
    this.reduceQtyBtn?.removeEventListener("click", this.reduceQuantity);
    this.increaseQtyBtn?.removeEventListener("click", this.increaseQuantity);
    this.qtyInput.removeEventListener("blur", this.handleBlur);
  }
}
customElements.define("quantity-selector", QuantitySelector);

class TabAccordion extends HTMLElement {
  mq = window.matchMedia("(min-width: 992px)");

  connectedCallback() {
    const onIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    onIdle(() => this.init());
  }

  init() {
    this.details = this.querySelectorAll("details");

    this.handleLayout = () => this.updateLayout();

    this.mq.addEventListener("change", this.handleLayout);
    this.details.forEach((d) => {
      d.addEventListener("toggle", this.handleLayout);
    });

    this.updateLayout();
  }

  updateLayout() {
    if (this.mq.matches) {
      const openDetail = this.querySelector("details[open]");
      const content = openDetail?.querySelector(":scope > :not(summary)");
      this.style.marginBottom = content ? content.offsetHeight + "px" : "";
    } else {
      this.style.marginBottom = "";
    }
  }

  disconnectedCallback() {
    this.mq.removeEventListener("change", this.handleLayout);
    this.details?.forEach((d) => {
      d.removeEventListener("toggle", this.handleLayout);
    });
  }
}

customElements.define("tab-accordion", TabAccordion);

class DialogModal extends BaseHTMLElement {
  get panel() {
    /** @type {HTMLDialogElement | HTMLDivElement | null} */
    return this.querySelector("[data-role=dialogBody]");
  }
  get isDialog() {
    return typeof this.panel?.showModal === "function";
  }
  get activeClass() {
    return this.getAttribute("data-active-class");
  }

  close() {
    document.body.classList.remove("overflow-hidden");
    this.panel?.classList.remove(this.activeClass);
    if (this.isDialog) {
      this.panel?.addEventListener("transitionend", () => this.panel?.close(), {
        once: true,
      });
    }
  }
  handleBackdropClick = (e) => {
    if (e.target === this.panel) {
      this.close();
    }
  };
  registerEvents = () => {
    this.panel?.addEventListener("click", this.handleBackdropClick);
    this.panel?.addEventListener(
      "cancel",
      (e) => {
        e.preventDefault();
        this.close();
      },
      { once: true },
    );
  };
  showLoader = () => {
    /** @type {HTMLTemplateElement | null} */
    const loaderTemplate = this.querySelector("[data-role=dialogLoader]");

    /** @type {HTMLDivElement | null} */
    const loaderContent = loaderTemplate?.content.cloneNode(true);

    /** @type {HTMLDivElement | null} */
    const existingContent = this.querySelector("[data-role=dialogContent]");

    const requestPath = this.getAttribute("data-request-path");

    if (!loaderContent) {
      return;
    }
    if (requestPath) {
      /** @type {LazySection | null} */
      loaderContent
        .querySelector("lazy-section")
        .setAttribute("data-request-path", requestPath);
    }

    if (existingContent) {
      existingContent.replaceWith(loaderContent);
    } else {
      loaderTemplate.after(loaderContent);
    }
  };
  open = () => {
    document.body.classList.add("overflow-hidden");
    this.showLoader();
    if (this.isDialog) {
      this.panel?.showModal();
      this.registerEvents();
    }
    requestAnimationFrame(() => this.panel?.classList.add(this.activeClass));
  };

  toggle = () => {
    if (this.panel?.classList.contains(this.activeClass)) {
      this.close();
    } else {
      this.open();
    }
  };

  disconnectedCallback() {
    this.close();
    this.panel?.removeEventListener("click", this.handleBackdropClick);
  }
}
customElements.define("dialog-modal", DialogModal);

class DialogToggle extends BaseHTMLElement {
  toggle() {
    const dialogId = this.getAttribute("data-target");
    const requestPath = this.getAttribute("data-request-path");

    /** @type {DialogModal | null} */
    const dialogModal =
      document.getElementById(dialogId) ?? this.closest("dialog-modal");
    if (requestPath) {
      dialogModal?.setAttribute("data-request-path", requestPath);
    }
    dialogModal?.toggle();
  }
  init = () => {
    /** @type {HTMLButtonElement | null} */
    this.toggleButton = this.querySelector("[data-role=toggleDialog]");
    this._handleClick = this.toggle.bind(this);
    this.toggleButton?.addEventListener("click", this._handleClick);
  };
  disconnectedCallback() {
    this.toggleButton?.removeEventListener("click", this._handleClick);
  }
}
customElements.define("dialog-toggle", DialogToggle);

class PopupToggle extends DialogToggle {
  toggle() {
    if (isMobile()) {
      super.toggle();
    } else {
      /** @type {HTMLInputElement | null} */
      this.closest("[data-role=popupBase]")
        ?.querySelector("[data-role=popupController]")
        ?.click();
    }
  }
}

customElements.define("popup-toggle", PopupToggle);

class ToggleButton extends BaseHTMLElement {
  toggle = () => {
    const targetId = this.getAttribute("data-target");
    const targetClass = this.getAttribute("data-class");
    if (!targetClass || !targetId) {
      return;
    }
    document.getElementById(targetId)?.classList.toggle(targetClass);
  };
  init = () => {
    /** @type {HTMLButtonElement | null} */
    this.toggleButton = this.querySelector("[data-role=toggleButton]");
    this.toggleButton?.addEventListener("click", this.toggle);
  };
  disconnectedCallback() {
    this.toggleButton?.removeEventListener("click", this.toggle);
  }
}
customElements.define("toggle-button", ToggleButton);
